import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../AuthContext';
import configData from '../../configure';
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// ─── Constants ────────────────────────────────────────────────────────────────

const KV_ORG_ID = '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83';

const FACULTY_TYPES = [
  { value: "PRIMARY_TEACHER", label: "Primary Teacher" },
  { value: "MIDDLE_SCHOOL_TEACHER", label: "Middle School Teacher" },
  { value: "HIGH_SCHOOL_TEACHER", label: "High School Teacher" },
  { value: "SENIOR_HIGH_SCHOOL_TEACHER", label: "Senior High School Teacher" },
  { value: "HEADMASTER", label: "Headmaster" },
  { value: "PRINCIPAL", label: "Principal" },
  { value: "ASSISTANT_PROFESSOR", label: "Assistant Professor" },
  { value: "ASSOCIATE_PROFESSOR", label: "Associate Professor" },
  { value: "FULL_PROFESSOR", label: "Full Professor" },
  { value: "LECTURER", label: "Lecturer" },
  { value: "INSTRUCTOR", label: "Instructor" },
  { value: "ADJUNCT", label: "Adjunct" },
  { value: "VISITING_PROFESSOR", label: "Visiting Professor" },
  { value: "EMERITUS", label: "Emeritus" },
  { value: "HEAD_OF_DEPARTMENT", label: "Head of Department" },
  { value: "DEAN", label: "Dean" },
  { value: "DIRECTOR", label: "Director" },
  { value: "VICE_CHANCELLOR", label: "Vice Chancellor" },
  { value: "CHANCELLOR", label: "Chancellor" },
  { value: "NIRVAHAK", label: "Nirvahak" },
  { value: "PARYAVEKSHAK", label: "Paryavekshak" },
  { value: "SAMANVAYKARTA", label: "Samanvaykarta" },
];

// ─── Helper: build display name ───────────────────────────────────────────────

const buildName = (u) =>
  [u?.firstName, u?.middleName, u?.lastName].filter(Boolean).join(' ').trim() ||
  u?.email || '';

// ─── FacultyDetails Component ─────────────────────────────────────────────────

function FacultyDetails({
  selectedUser,
  handleChange,
  editedUser,
  isEditing,
  handleEditClick,
  handleSaveClick,
  handleCancelClick,
  loading,
  orgId,
  axios,
}) {
  const { hasPermission, userRole, user: loggedInUser, facultyInfo: loggedInFacultyInfo } = useAuth();
  console.log('Selected User:', selectedUser);

  const isKVOrg = orgId === KV_ORG_ID;

  // ── Dates ──
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toISOString().split('T')[0];
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-GB');
  };

  // ── Current faculty type (view vs edit) ──
  const viewFacultyType =
    selectedUser?.rawData?.facultyInfo?.facultyType ||
    selectedUser?.facultyInfo?.facultyType ||
    '';
  const editFacultyType = editedUser?.facultyInfo?.facultyType || '';
  const activeFacultyType = isEditing ? editFacultyType : viewFacultyType;

  const latVal = selectedUser?.rawData?.user?.additionalAttributes?.latitude || selectedUser?.user?.additionalAttributes?.latitude || '';
  const lngVal = selectedUser?.rawData?.user?.additionalAttributes?.longitude || selectedUser?.user?.additionalAttributes?.longitude || '';
  const areaVal = selectedUser?.rawData?.user?.additionalAttributes?.area || selectedUser?.user?.additionalAttributes?.area || '';

  const getAssignedCenters = () => {
    const assignedStr = selectedUser?.rawData?.user?.additionalAttributes?.assignedClasses || 
                       selectedUser?.user?.additionalAttributes?.assignedClasses || 
                       selectedUser?.additionalAttributes?.assignedClasses || 
                       editedUser?.user?.additionalAttributes?.assignedClasses || '';
    if (!assignedStr) return [];
    return assignedStr.split(',')
      .map(c => c.trim())
      .filter(c => c.length > 0 && c.toLowerCase() !== 'main-region' && c.toLowerCase() !== 'main region');
  };

  const targetUserId = selectedUser?.rawData?.user?.userId || selectedUser?.user?.userId || selectedUser?.userId;
  const loggedInUserId = loggedInUser?.userId;

  const canEdit =
    userRole === 'ADMIN' ||
    (loggedInUserId && targetUserId && loggedInUserId.toLowerCase().trim() === targetUserId.toLowerCase().trim()) ||
    (userRole === 'FACULTY' &&
      (loggedInFacultyInfo?.facultyType === 'SAMANVAYKARTA' &&
        (viewFacultyType === 'PARYAVEKSHAK' || viewFacultyType === 'NIRVAHAK'))
    ) ||
    (userRole === 'FACULTY' &&
      (loggedInFacultyInfo?.facultyType === 'PARYAVEKSHAK' &&
        viewFacultyType === 'NIRVAHAK')
    ) ||
    hasPermission('FACULTY', 'EDIT') ||
    hasPermission('FACULTY', 'UPDATE');

  // ── Supervisor name (view mode) ───────────────────────────────────────────
  const supervisorIdValue =
    selectedUser?.rawData?.facultyInfo?.supervisorId ||
    selectedUser?.facultyInfo?.supervisorId ||
    '';

  const [supervisorName, setSupervisorName] = useState('');
  const [supervisorNameLoading, setSupervisorNameLoading] = useState(false);

  useEffect(() => {
    if (!supervisorIdValue || !axios || viewFacultyType === 'SAMANVAYKARTA') {
      setSupervisorName('');
      return;
    }
    let cancelled = false;
    setSupervisorNameLoading(true);
    axios
      .get(`${configData.GET_USER_DETAILS}/${supervisorIdValue}`)
      .then((res) => {
        if (!cancelled) {
          const u = res.data?.user || res.data;
          setSupervisorName(buildName(u) || supervisorIdValue);
        }
      })
      .catch(() => {
        if (!cancelled) setSupervisorName(supervisorIdValue); // fallback to ID
      })
      .finally(() => {
        if (!cancelled) setSupervisorNameLoading(false);
      });
    return () => { cancelled = true; };
  }, [supervisorIdValue, viewFacultyType, axios]);

  // ── Supervisor dropdown (edit mode) ──────────────────────────────────────
  // What type of supervisor to fetch based on the current faculty type being edited
  const supervisorFacultyType = React.useMemo(() => {
    return editFacultyType === 'NIRVAHAK'
      ? ['PARYAVEKSHAK', 'SAMANVAYKARTA']
      : editFacultyType === 'PARYAVEKSHAK'
      ? ['SAMANVAYKARTA']
      : [];
  }, [editFacultyType]);

  const supervisorDisabled = editFacultyType === 'SAMANVAYKARTA';
  const supervisorIsDropdown = isKVOrg && supervisorFacultyType.length > 0;

  const [supervisorOptions, setSupervisorOptions] = useState([]);
  const [supervisorLoading, setSupervisorLoading] = useState(false);
  const supervisorFetchRef = useRef(null);

  useEffect(() => {
    if (!isEditing || !supervisorIsDropdown || !orgId || !axios) {
      setSupervisorOptions([]);
      return;
    }

    if (supervisorFetchRef.current) supervisorFetchRef.current.cancelled = true;
    const guard = { cancelled: false };
    supervisorFetchRef.current = guard;

    const fetchSupervisors = async () => {
      setSupervisorLoading(true);
      try {
        let lastEvaluatedKey = null;
        let allItems = [];
        const seenKeys = new Set();
        do {
          const res = await axios.get(`${configData.GET_USERS_BY_TYPE}/${orgId}`, {
            params: {
              userType: 'FACULTY',
              pageSize: 300,
              lastEvaluatedKey: lastEvaluatedKey || undefined
            },
            headers: { accept: 'application/hal+json' },
          });
          if (guard.cancelled) return;

          const rawData = res.data;
          const pageItems = Array.isArray(rawData)
            ? rawData
            : Array.isArray(rawData?.items)
            ? rawData.items
            : Array.isArray(rawData?.content)
            ? rawData.content
            : [];
          allItems = allItems.concat(pageItems);

          lastEvaluatedKey = rawData?.lastEvaluatedKey;
          if (lastEvaluatedKey && seenKeys.has(lastEvaluatedKey)) break;
          if (lastEvaluatedKey) seenKeys.add(lastEvaluatedKey);
        } while (lastEvaluatedKey);

        const filtered = allItems
          .filter((item) => {
            const type = item?.facultyInfo?.facultyType;
            return supervisorFacultyType.includes(type);
          })
          .map((item) => ({
            id: item?.user?.userId || '',
            name: buildName(item?.user),
            employeeId: item?.facultyInfo?.employeeId || '',
            phone: item?.user?.phoneNumber || '',
          }))
          .filter((s) => !!s.id);

        setSupervisorOptions(filtered);
      } catch (err) {
        if (!guard.cancelled) setSupervisorOptions([]);
      } finally {
        if (!guard.cancelled) setSupervisorLoading(false);
      }
    };

    fetchSupervisors();
    return () => { guard.cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supervisorFacultyType, supervisorIsDropdown, orgId, isEditing]);

  // ── Supervisor label in edit dropdown ──
  const supervisorDropdownLabel =
    editFacultyType === 'NIRVAHAK'
      ? 'Supervisor (Paryavekshak/Samanvaykarta)'
      : 'Supervisor (Samanvaykarta)';

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="registration-container">
      <div className="studentdetailedit">
        <h2>Faculty Details</h2>
        <div className="button-group">
          {isEditing ? (
            <>
              <button
                className="Save-profile-custom-button"
                onClick={handleSaveClick}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
              <button
                className="Cancel-profile-custom-button"
                onClick={handleCancelClick}
                disabled={loading}
              >
                Cancel
              </button>
            </>
          ) : (
            canEdit && (
              <button
                className="discord-profile-custom-button"
                onClick={handleEditClick}
              >
                Edit Profile
              </button>
            )
          )}
        </div>
      </div>

      <div className="registration-form">
        {/* Basic Information */}
        <h3>Basic Information</h3>
        <div className="basicinfostudentdetail">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name: <span className="required-star">*</span></label>
              {isEditing ? (
                <input
                  type="text"
                  id="firstName"
                  name="user.firstName"
                  value={editedUser?.user?.firstName || ''}
                  onChange={handleChange}
                  placeholder="First Name"
                />
              ) : (
                <span className="InfoValue">{selectedUser?.rawData?.user?.firstName || selectedUser?.firstName || 'N/A'}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="middleName">Middle Name:</label>
              {isEditing ? (
                <input
                  type="text"
                  id="middleName"
                  name="user.middleName"
                  value={editedUser?.user?.middleName || ''}
                  onChange={handleChange}
                  placeholder="Middle Name"
                />
              ) : (
                <span className="InfoValue">{selectedUser?.rawData?.user?.middleName || selectedUser?.middleName || 'N/A'}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last Name: <span className="required-star">*</span></label>
              {isEditing ? (
                <input
                  type="text"
                  id="lastName"
                  name="user.lastName"
                  value={editedUser?.user?.lastName || ''}
                  onChange={handleChange}
                  placeholder="Last Name"
                />
              ) : (
                <span className="InfoValue">{selectedUser?.rawData?.user?.lastName || selectedUser?.lastName || 'N/A'}</span>
              )}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="gender">Gender: <span className="required-star">*</span></label>
              {isEditing ? (
                <select
                  id="gender"
                  name="user.gender"
                  value={editedUser?.user?.gender || ''}
                  onChange={handleChange}
                >
                  <option value="">Select Gender</option>
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                  <option value="OTHER">Other</option>
                </select>
              ) : (
                <span className="InfoValue">{selectedUser?.rawData?.user?.gender || selectedUser?.gender || 'N/A'}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="dob">Date of Birth: <span className="required-star">*</span></label>
              {isEditing ? (
                <input
                  type="date"
                  id="dob"
                  name="user.dateOfBirth"
                  value={formatDateForInput(editedUser?.user?.dateOfBirth) || ''}
                  onChange={handleChange}
                />
              ) : (
                <span className="InfoValue">{selectedUser?.rawData?.user?.dateOfBirth ? formatDateForDisplay(selectedUser.rawData.user.dateOfBirth) : 'N/A'}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="placeOfBirth">Place of Birth:</label>
              {isEditing ? (
                <input
                  type="text"
                  id="placeOfBirth"
                  name="user.placeOfBirth"
                  value={editedUser?.user?.placeOfBirth || ''}
                  onChange={handleChange}
                  placeholder="Place of Birth"
                />
              ) : (
                <span className="InfoValue">{selectedUser?.rawData?.user?.placeOfBirth || selectedUser?.placeOfBirth || 'N/A'}</span>
              )}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="bloodGroup">Blood Group:</label>
              {isEditing ? (
                <select
                  id="bloodGroup"
                  name="user.bloodGroup"
                  value={editedUser?.user?.bloodGroup || ''}
                  onChange={handleChange}
                >
                  <option value="">Select Blood Group</option>
                  <option value="A_POSITIVE">A+</option>
                  <option value="A_NEGATIVE">A-</option>
                  <option value="B_POSITIVE">B+</option>
                  <option value="B_NEGATIVE">B-</option>
                  <option value="O_POSITIVE">O+</option>
                  <option value="O_NEGATIVE">O-</option>
                  <option value="AB_POSITIVE">AB+</option>
                  <option value="AB_NEGATIVE">AB-</option>
                </select>
              ) : (
                <span className="InfoValue">{selectedUser?.rawData?.user?.bloodGroup || selectedUser?.bloodGroup || 'N/A'}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="nationality">Nationality:</label>
              {isEditing ? (
                <input
                  type="text"
                  id="nationality"
                  name="user.nationality"
                  value={editedUser?.user?.nationality || ''}
                  onChange={handleChange}
                  placeholder="Nationality"
                />
              ) : (
                <span className="InfoValue">{selectedUser?.rawData?.user?.nationality || selectedUser?.nationality || 'N/A'}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="religion">Religion:</label>
              {isEditing ? (
                <input
                  type="text"
                  id="religion"
                  name="user.religion"
                  value={editedUser?.user?.religion || ''}
                  onChange={handleChange}
                  placeholder="Religion"
                />
              ) : (
                <span className="InfoValue">{selectedUser?.rawData?.user?.religion || selectedUser?.religion || 'N/A'}</span>
              )}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">Category:</label>
              {isEditing ? (
                <input
                  type="text"
                  id="category"
                  name="user.category"
                  value={editedUser?.user?.category || ''}
                  onChange={handleChange}
                  placeholder="Category"
                />
              ) : (
                <span className="InfoValue">{selectedUser?.rawData?.user?.category || selectedUser?.category || 'N/A'}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="email">Email: <span className="required-star">*</span></label>
              {isEditing ? (
                <input
                  type="email"
                  id="email"
                  name="user.email"
                  value={editedUser?.user?.email || ''}
                  onChange={handleChange}
                  placeholder="example@gmail.com"
                />
              ) : (
                <span className="InfoValue">{selectedUser?.rawData?.user?.email || selectedUser?.email || 'N/A'}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="phoneNumber">Phone Number: <span className="required-star">*</span></label>
              {isEditing ? (
                <input
                  type="text"
                  id="phoneNumber"
                  name="user.phoneNumber"
                  value={editedUser?.user?.phoneNumber || ''}
                  onChange={handleChange}
                  placeholder="Phone Number"
                />
              ) : (
                <span className="InfoValue">{selectedUser?.rawData?.user?.phoneNumber || selectedUser?.phoneNumber || 'N/A'}</span>
              )}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="lastFourDigitsOfAadhaarNumber">Last Four Digits of Aadhaar:</label>
              {isEditing ? (
                <input
                  type="text"
                  id="lastFourDigitsOfAadhaarNumber"
                  name="user.lastFourDigitsOfAadhaarNumber"
                  value={editedUser?.user?.lastFourDigitsOfAadhaarNumber || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(-4);
                    handleChange({ target: { name: 'user.lastFourDigitsOfAadhaarNumber', value } });
                  }}
                  placeholder="Last Four Digits"
                  maxLength="4"
                />
              ) : (
                <span className="InfoValue">{selectedUser?.rawData?.user?.lastFourDigitsOfAadhaarNumber || selectedUser?.lastFourDigitsOfAadhaarNumber || 'N/A'}</span>
              )}
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <h3>Contact Information</h3>
        <div className="basicinfostudentdetail">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="currentAddress1">Address:</label>
              {isEditing ? (
                <input
                  type="text"
                  id="currentAddress1"
                  name="user.addressDetails.0.addressLine1"
                  value={editedUser?.user?.addressDetails?.[0]?.addressLine1 || ''}
                  onChange={handleChange}
                  placeholder="Address Line 1"
                />
              ) : (
                <span className="InfoValue">{selectedUser?.rawData?.user?.addressDetails?.[0]?.addressLine1 || 'N/A'}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="city">City: <span className="required-star">*</span></label>
              {isEditing ? (
                <input
                  type="text"
                  id="city"
                  name="user.addressDetails.0.city"
                  value={editedUser?.user?.addressDetails?.[0]?.city || ''}
                  onChange={handleChange}
                  placeholder="City"
                />
              ) : (
                <span className="InfoValue">{selectedUser?.rawData?.user?.addressDetails?.[0]?.city || 'N/A'}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="state">State:</label>
              {isEditing ? (
                <input
                  type="text"
                  id="state"
                  name="user.addressDetails.0.state"
                  value={editedUser?.user?.addressDetails?.[0]?.state || ''}
                  onChange={handleChange}
                  placeholder="Enter State"
                />
              ) : (
                <span className="InfoValue">{selectedUser?.rawData?.user?.addressDetails?.[0]?.state || 'N/A'}</span>
              )}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="country">Country:</label>
              {isEditing ? (
                <input
                  type="text"
                  id="country"
                  name="user.addressDetails.0.country"
                  value={editedUser?.user?.addressDetails?.[0]?.country || ''}
                  onChange={handleChange}
                  placeholder="Enter Country"
                />
              ) : (
                <span className="InfoValue">{selectedUser?.rawData?.user?.addressDetails?.[0]?.country || 'N/A'}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="pincode">Pincode:</label>
              {isEditing ? (
                <input
                  type="text"
                  id="pincode"
                  name="user.addressDetails.0.pinCode"
                  value={editedUser?.user?.addressDetails?.[0]?.pinCode || ''}
                  onChange={handleChange}
                  placeholder="Enter Pincode"
                />
              ) : (
                <span className="InfoValue">{selectedUser?.rawData?.user?.addressDetails?.[0]?.pinCode || 'N/A'}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="alternatePhoneNumber">Alternate Contact Number:</label>
              {isEditing ? (
                <input
                  type="text"
                  id="alternatePhoneNumber"
                  name="user.addressDetails.0.alternatePhoneNumber"
                  value={editedUser?.user?.addressDetails?.[0]?.alternatePhoneNumber || ''}
                  onChange={handleChange}
                  placeholder="Alternate Contact Number"
                />
              ) : (
                <span className="InfoValue">{selectedUser?.rawData?.user?.addressDetails?.[0]?.alternatePhoneNumber || 'N/A'}</span>
              )}
            </div>
          </div>
          {activeFacultyType === 'NIRVAHAK' && (
            <>
              {isEditing ? (
                <div>
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', marginBottom: '16px', backgroundColor: '#f8fafc' }}>
                    <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#1e293b', marginBottom: '8px', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px' }}>
                      General / Fallback Location
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="area">Area:</label>
                        <input
                          type="text"
                          id="area"
                          name="user.additionalAttributes.area"
                          value={editedUser?.user?.additionalAttributes?.area || ''}
                          onChange={handleChange}
                          placeholder="Area"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="latitude">Latitude:</label>
                        <input
                          type="text"
                          id="latitude"
                          name="user.additionalAttributes.latitude"
                          value={editedUser?.user?.additionalAttributes?.latitude || ''}
                          onChange={handleChange}
                          placeholder="Latitude"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="longitude">Longitude:</label>
                        <input
                          type="text"
                          id="longitude"
                          name="user.additionalAttributes.longitude"
                          value={editedUser?.user?.additionalAttributes?.longitude || ''}
                          onChange={handleChange}
                          placeholder="Longitude"
                        />
                      </div>
                    </div>
                    <div style={{ marginTop: '8px' }}>
                      <button
                        type="button"
                        onClick={() => {
                          if (navigator.geolocation) {
                            navigator.geolocation.getCurrentPosition(
                              (position) => {
                                handleChange({
                                  target: {
                                    name: 'user.additionalAttributes.latitude',
                                    value: String(position.coords.latitude),
                                  },
                                });
                                handleChange({
                                  target: {
                                    name: 'user.additionalAttributes.longitude',
                                    value: String(position.coords.longitude),
                                  },
                                });
                              },
                              (error) => {
                                alert('Failed to get location: ' + error.message);
                              }
                            );
                          } else {
                            alert('Geolocation is not supported by this browser.');
                          }
                        }}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#e0ecfb',
                          border: '1.5px solid #0B4A8B',
                          borderRadius: '6px',
                          color: '#0B4A8B',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '13px'
                        }}
                      >
                        📍 Use Current Location
                      </button>
                    </div>
                  </div>


                </div>
              ) : (
                <div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Area:</label>
                      <span className="InfoValue">{areaVal || 'N/A'}</span>
                    </div>
                    <div className="form-group">
                      <label>General Location:</label>
                      <span className="InfoValue">
                        {latVal && lngVal ? `${latVal}, ${lngVal}` : 'N/A'}
                        {latVal && lngVal && (
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${latVal},${lngVal}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ marginLeft: '10px', color: '#0B4A8B', textDecoration: 'underline', fontSize: '13px', fontWeight: 'bold' }}
                          >
                            🗺️ View on Maps
                          </a>
                        )}
                      </span>
                    </div>
                  </div>


                </div>
              )}
            </>
          )}
        </div>

        {/* Faculty Information */}
        <h3>Faculty Information</h3>
        <div className="basicinfostudentdetail">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="facultyType">Faculty Type: <span className="required-star">*</span></label>
              {isEditing ? (
                <select
                  id="facultyType"
                  name="facultyInfo.facultyType"
                  value={editedUser?.facultyInfo?.facultyType || ''}
                  onChange={handleChange}
                >
                  <option value="">Select Faculty Type</option>
                  {FACULTY_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="InfoValue">{selectedUser?.rawData?.facultyInfo?.facultyType || 'N/A'}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="employeeId">Employee ID:</label>
              {isEditing ? (
                <input
                  type="text"
                  id="employeeId"
                  name="facultyInfo.employeeId"
                  value={editedUser?.facultyInfo?.employeeId || ''}
                  onChange={handleChange}
                  placeholder="Employee ID"
                />
              ) : (
                <span className="InfoValue">{selectedUser?.rawData?.facultyInfo?.employeeId || 'N/A'}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="designation">Designation:</label>
              {isEditing ? (
                <input
                  type="text"
                  id="designation"
                  name="facultyInfo.designation"
                  value={editedUser?.facultyInfo?.designation || ''}
                  onChange={handleChange}
                  placeholder="Designation"
                />
              ) : (
                <span className="InfoValue">{selectedUser?.rawData?.facultyInfo?.designation || 'N/A'}</span>
              )}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="department">Department:</label>
              {isEditing ? (
                <input
                  type="text"
                  id="department"
                  name="facultyInfo.department"
                  value={editedUser?.facultyInfo?.department || ''}
                  onChange={handleChange}
                  placeholder="Department"
                />
              ) : (
                <span className="InfoValue">{selectedUser?.rawData?.facultyInfo?.department || 'N/A'}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="joiningDate">Joining Date:</label>
              {isEditing ? (
                <input
                  type="date"
                  id="joiningDate"
                  name="facultyInfo.joiningDate"
                  value={formatDateForInput(editedUser?.facultyInfo?.joiningDate) || ''}
                  onChange={handleChange}
                />
              ) : (
                <span className="InfoValue">{selectedUser?.rawData?.facultyInfo?.joiningDate ? formatDateForDisplay(selectedUser.rawData.facultyInfo.joiningDate) : 'N/A'}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="facultyContractType">Contract Type:</label>
              {isEditing ? (
                <select
                  id="facultyContractType"
                  name="facultyInfo.facultyContractType"
                  value={editedUser?.facultyInfo?.facultyContractType || ''}
                  onChange={handleChange}
                >
                  <option value="">Select Contract Type</option>
                  <option value="PERMANENT">Permanent</option>
                  <option value="TEMPORARY">Temporary</option>
                  <option value="CONTRACT">Contract</option>
                </select>
              ) : (
                <span className="InfoValue">{selectedUser?.rawData?.facultyInfo?.facultyContractType || 'N/A'}</span>
              )}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="responsibilities">Responsibilities:</label>
              {isEditing ? (
                <input
                  type="text"
                  id="responsibilities"
                  name="facultyInfo.responsibilities"
                  value={
                    Array.isArray(editedUser?.facultyInfo?.responsibilities)
                      ? editedUser.facultyInfo.responsibilities.join(', ')
                      : ''
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    const responsibilities = value.split(',').map((item) => item.trim()).filter((item) => item !== '');
                    handleChange({ target: { name: 'facultyInfo.responsibilities', value: responsibilities } });
                  }}
                  placeholder="Responsibilities"
                />
              ) : (
                <span className="InfoValue">
                  {Array.isArray(selectedUser?.rawData?.facultyInfo?.responsibilities)
                    ? selectedUser.rawData.facultyInfo.responsibilities.join(', ') || 'N/A'
                    : 'N/A'}
                </span>
              )}
            </div>

            {/* ── Supervisor Field — hierarchy-aware ── */}
            <div className="form-group">
              <label htmlFor="supervisorId">
                Supervisor:
                {activeFacultyType !== 'SAMANVAYKARTA' && <span className="required-star"> *</span>}
              </label>

              {/* ── VIEW MODE ── */}
              {!isEditing && (
                <span className="InfoValue">
                  {viewFacultyType === 'SAMANVAYKARTA' ? (
                    <span style={{ color: '#1565c0', fontStyle: 'italic', fontSize: 13 }}>Top level — no supervisor</span>
                  ) : supervisorNameLoading ? (
                    <CircularProgress size={14} style={{ marginLeft: 4 }} />
                  ) : supervisorName ? (
                    <span style={{ fontWeight: 600 }}>{supervisorName}</span>
                  ) : (
                    'N/A'
                  )}
                </span>
              )}

              {/* ── EDIT MODE ── */}
              {isEditing && (
                <>
                  {supervisorDisabled ? (
                    // SAMANVAYKARTA — no supervisor
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{
                        bgcolor: '#e8f4fd',
                        border: '1px solid #90caf9',
                        borderRadius: 2,
                        px: 2,
                        py: 1.2,
                        minHeight: 40,
                      }}
                    >
                      <InfoOutlinedIcon sx={{ color: '#1565c0', fontSize: 18 }} />
                      <span style={{ fontSize: 13, color: '#1565c0', fontWeight: 600 }}>
                        Samanvaykarta is the top level — no supervisor required.
                      </span>
                    </Box>
                  ) : supervisorIsDropdown ? (
                    // PARYAVEKSHAK or NIRVAHAK — dropdown
                    <FormControl fullWidth size="small">
                      <InputLabel>{supervisorDropdownLabel}</InputLabel>
                      <Select
                        id="supervisorId"
                        name="facultyInfo.supervisorId"
                        value={editedUser?.facultyInfo?.supervisorId || ''}
                        label={supervisorDropdownLabel}
                        onChange={handleChange}
                        disabled={supervisorLoading}
                        endAdornment={
                          supervisorLoading
                            ? <CircularProgress size={16} sx={{ mr: 2 }} />
                            : null
                        }
                      >
                        <MenuItem value="">
                          <em style={{ color: '#94a3b8' }}>
                            {supervisorLoading
                              ? 'Loading supervisors…'
                              : supervisorOptions.length === 0
                              ? 'No supervisors found'
                              : 'Select supervisor'}
                          </em>
                        </MenuItem>
                        <MenuItem value="none" sx={{ fontWeight: 600, color: '#dc2626' }}>
                          None
                        </MenuItem>
                        {supervisorOptions.map((sup) => (
                          <MenuItem key={sup.id} value={sup.id}>
                            <Box>
                              <span style={{ fontWeight: 600 }}>{sup.name}</span>
                              {sup.employeeId && (
                                <Chip
                                  label={sup.employeeId}
                                  size="small"
                                  sx={{ ml: 1, fontSize: 11, height: 18 }}
                                />
                              )}
                              {sup.phone && (
                                <span style={{ fontSize: 12, color: '#64748b', marginLeft: 6 }}>
                                  {sup.phone}
                                </span>
                              )}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                      {supervisorOptions.length === 0 && !supervisorLoading && editFacultyType && (
                        <FormHelperText sx={{ color: 'warning.main' }}>
                          No {supervisorFacultyType?.map(t => t.toLowerCase()).join('/')} registered yet. Register one first.
                        </FormHelperText>
                      )}
                    </FormControl>
                  ) : (
                    // Non-KV org — plain text
                    <input
                      type="text"
                      id="supervisorId"
                      name="facultyInfo.supervisorId"
                      value={editedUser?.facultyInfo?.supervisorId || ''}
                      onChange={handleChange}
                      placeholder="Supervisor ID"
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FacultyDetails;
